import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth, useCan } from './auth/AuthContext';
import { resolveDefaultPath } from './layout/navConfig';
import LoginPage from './pages/LoginPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import PublicPayPage from './pages/PublicPayPage';
import AppShell from './layout/AppShell';
// Личный кабинет родителя — отдельный layout/маршрутный поддерев.
import ParentShell from './parent/ParentShell';
import ParentHomePage from './parent/ParentHomePage';
import ParentDiaryPage from './parent/ParentDiaryPage';
import ParentGradesPage from './parent/ParentGradesPage';
import ParentSchedulePage from './parent/ParentSchedulePage';
import ParentPaymentsPage from './parent/ParentPaymentsPage';
import ParentMessagesPage from './parent/ParentMessagesPage';
import ParentProfilePage from './parent/ParentProfilePage';
import ParentExtrasPage from './parent/ParentExtrasPage';
import ParentExtracurricularPage from './parent/ParentExtracurricularPage';
import TeacherShell from './teacher/TeacherShell';
import TeacherSchedulePage from './teacher/TeacherSchedulePage';
import TeacherJournalPage from './teacher/TeacherJournalPage';
import TeacherExtracurricularPage from './teacher/TeacherExtracurricularPage';
import TeacherExtracurricularSessionPage from './teacher/TeacherExtracurricularSessionPage';
import TeacherExtraSchedulePage from './teacher/TeacherExtraSchedulePage';
import TeacherReportsPage from './teacher/TeacherReportsPage';
import TeacherExtrasMonthlyReport from './teacher/TeacherExtrasMonthlyReport';
import StudentsPage from './pages/students/StudentsPage';
import UserSettingsPage from './pages/me/UserSettingsPage';
import SettingsPage from './pages/settings/SettingsPage';
import SettingsForm from './pages/settings/SettingsForm';
import ServicesPanel from './pages/settings/ServicesPanel';
import DdsArticlesPanel from './pages/settings/DdsArticlesPanel';
import SkudSettingsPanel from './pages/settings/SkudSettingsPanel';
import SkudPersonsPage from './pages/skud/SkudPersonsPage';
import MapsSettingsPanel from './pages/settings/MapsSettingsPanel';
import MailSettingsPanel from './pages/settings/MailSettingsPanel';
import TelephonySettingsPanel from './pages/settings/TelephonySettingsPanel';
import DataLockPanel from './pages/settings/DataLockPanel';
import YearsPage from './pages/years/YearsPage';
import TariffsPage from './pages/tariffs/TariffsPage';
import HolidaysPage from './pages/admin/HolidaysPage';
import TemplatesPage from './pages/admin/TemplatesPage';
// EmailTemplatesPage тянет TinyMCE (~1.5 МБ) — отдельным chunk'ом через lazy.
const EmailTemplatesPage  = lazy(() => import('./pages/admin/EmailTemplatesPage'));
// Та же причина — рассылки в редакторе тоже на TinyMCE.
const BroadcastsListPage         = lazy(() => import('./pages/broadcasts/BroadcastsListPage'));
const BroadcastEditPage          = lazy(() => import('./pages/broadcasts/BroadcastEditPage'));
const ScheduledBroadcastEditPage = lazy(() => import('./pages/broadcasts/ScheduledBroadcastEditPage'));
const MessagesPage       = lazy(() => import('./pages/messages/MessagesPage'));
import PermissionPresetsPage from './pages/admin/PermissionPresetsPage';
import StaffPage from './pages/staff/StaffPage';
import ParentsPage from './pages/parents/ParentsPage';
import ClassesPage from './pages/classes/ClassesPage';
import ClassDetailPage from './pages/classes/ClassDetailPage';
import TimetableSlotsPage from './pages/timetable/TimetableSlotsPage';
import SubjectsPage from './pages/subjects/SubjectsPage';
import SchedulePage from './pages/schedule/SchedulePage';
import RoomsPage from './pages/rooms/RoomsPage';
import JournalPage from './pages/journal/JournalPage';
import GradingPage from './pages/grading/GradingPage';
import AttendancePage from './pages/attendance/AttendancePage';
import CallsPage from './pages/calls/CallsPage';
import PaymentsPage from './pages/payments/PaymentsPage';
import ExtraSubjectsPage from './pages/extras/ExtraSubjectsPage';
import ExtraLessonTimesPage from './pages/extras/ExtraLessonTimesPage';
import ExtraLessonsPage from './pages/extras/ExtraLessonsPage';
import ExtraSchedulePage from './pages/extras/ExtraSchedulePage';
import ExtraChargesPage from './pages/extras/ExtraChargesPage';
import ExtracurricularSchedulePage from './pages/extracurricular/ExtracurricularSchedulePage';
import ExtracurricularSubjectsPage from './pages/extracurricular/ExtracurricularSubjectsPage';
import ExtracurricularTimesPage from './pages/extracurricular/ExtracurricularTimesPage';
import ExtracurricularSelectionsPage from './pages/extracurricular/ExtracurricularSelectionsPage';
import ExtracurricularSelectionResultsPage from './pages/extracurricular/ExtracurricularSelectionResultsPage';
import ExtracurricularSessionPage from './pages/extracurricular/ExtracurricularSessionPage';
import ReportsIndex from './pages/reports/ReportsIndex';
// Все отчёты — lazy. Они тянут chart.js (отдельный chunk через manualChunks
// в vite.config) и редко открываются — нет смысла грузить их в первый запрос.
const ExtrasByStudentMonthlyReport       = lazy(() => import('./pages/reports/ExtrasByStudentMonthlyReport'));
const ExtrasByTeacherMonthlyReport       = lazy(() => import('./pages/reports/ExtrasByTeacherMonthlyReport'));
const ExtracurricularByStudentMonthlyReport = lazy(() => import('./pages/reports/ExtracurricularByStudentMonthlyReport'));
const ExtracurricularByTeacherMonthlyReport = lazy(() => import('./pages/reports/ExtracurricularByTeacherMonthlyReport'));
const ExtracurricularPopularityReport    = lazy(() => import('./pages/reports/ExtracurricularPopularityReport'));
const AgreementsRegistryReport           = lazy(() => import('./pages/reports/AgreementsRegistryReport'));
const PaymentsByNewAgreementsReport      = lazy(() => import('./pages/reports/PaymentsByNewAgreementsReport'));
const UnpaidNextMonthReport              = lazy(() => import('./pages/reports/UnpaidNextMonthReport'));
const ExtrasBalancesReport               = lazy(() => import('./pages/reports/ExtrasBalancesReport'));
const ExtrasSubjectBalanceReport         = lazy(() => import('./pages/reports/ExtrasSubjectBalanceReport'));
const PaymentsChartReport                = lazy(() => import('./pages/reports/PaymentsChartReport'));
const UnassignedStudentsReport           = lazy(() => import('./pages/reports/UnassignedStudentsReport'));
const StudentsNoLkAccessReport           = lazy(() => import('./pages/reports/StudentsNoLkAccessReport'));
const ClassListsReport                   = lazy(() => import('./pages/reports/ClassListsReport'));
const StudentsOnMapReport                = lazy(() => import('./pages/reports/StudentsOnMapReport'));
const TerminationsReport                 = lazy(() => import('./pages/reports/TerminationsReport'));
const SkudFirstLastReport                = lazy(() => import('./pages/reports/SkudFirstLastReport'));
const SkudRouteReport                    = lazy(() => import('./pages/reports/SkudRouteReport'));
const ApiLogReport                       = lazy(() => import('./pages/reports/ApiLogReport'));
const WebserverLogReport                 = lazy(() => import('./pages/reports/WebserverLogReport'));
const PushOutboxReport                   = lazy(() => import('./pages/reports/PushOutboxReport'));
const AgeSexReport                       = lazy(() => import('./pages/reports/AgeSexReport'));
const VacationLessonsReport              = lazy(() => import('./pages/reports/VacationLessonsReport'));
const ParentsNoActiveChildrenReport      = lazy(() => import('./pages/reports/ParentsNoActiveChildrenReport'));
const SkudParentsUnlinkedReport          = lazy(() => import('./pages/reports/SkudParentsUnlinkedReport'));
const MobileDevicesReport                = lazy(() => import('./pages/reports/MobileDevicesReport'));
const UserActionsReport                  = lazy(() => import('./pages/reports/UserActionsReport'));

function isAreaPath(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // Cross-role guard: родитель попал в админскую часть → /parent.
  // Учитель попал в админскую часть → /teacher. Проверяем именно
  // `/parent` или `/parent/...`, а не `startsWith('/parent')` —
  // иначе под фильтр попадал бы и админский `/parents`.
  if (user.role === 'parent' && !isAreaPath(location.pathname, '/parent')) {
    return <Navigate to="/parent" replace />;
  }
  if (user.role === 'teacher' && !isAreaPath(location.pathname, '/teacher')) {
    return <Navigate to="/teacher" replace />;
  }
  return children;
}

function RequireParent({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // /parent — только для роли parent. Сотруднику здесь делать нечего;
  // отправляем в админку. Без этого админ, кликнув на /parent/...,
  // увидел бы кабинет с чужими данными.
  if (user.role !== 'parent') return <Navigate to="/students" replace />;
  return children;
}

function RequireTeacher({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // /teacher — для учителей, а также админов с can_teach=1 (у них
  // тоже могут быть назначены классы / уроки).
  const isAdmin = user.role === 'administrator';
  if (user.role !== 'teacher' && !(isAdmin && user.canTeach)) {
    return <Navigate to={user.role === 'parent' ? '/parent' : '/students'} replace />;
  }
  return children;
}

function RedirectIfAuth({ children }) {
  const { user, loading } = useAuth();
  const can = useCan();
  if (loading) return <FullPageLoader />;
  // Уже залогинен — отправляем туда, где ему место (по роли). Для админ-части
  // учитываем персональную «стартовую страницу» (с валидацией по правам).
  if (user) {
    const home = user.role === 'parent' ? '/parent'
      : user.role === 'teacher' ? '/teacher'
      : resolveDefaultPath(can);
    return <Navigate to={home} replace />;
  }
  return children;
}

// Корень `/` для админ-части: ведём на персональную стартовую страницу
// (или '/students' по умолчанию). Родителя/учителя сюда не пускает
// RequireAuth — их разворачивает в их зону раньше.
function HomeRedirect() {
  const can = useCan();
  return <Navigate to={resolveDefaultPath(can)} replace />;
}

function FullPageLoader() {
  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center',
      color: 'var(--fg-muted)', fontSize: 13,
    }}>
      Загрузка…
    </div>
  );
}

function NotImplemented({ title }) {
  return (
    <div style={{ padding: 32, color: 'var(--fg)' }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h2>
      <p style={{ color: 'var(--fg-muted)' }}>Эта страница ещё не сделана.</p>
    </div>
  );
}

// Заглушка для вкладок ЛК родителя, пока экраны не наполнены.
function ParentPlaceholder({ title }) {
  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{title}</h1>
      <p style={{ color: '#6b7280', fontSize: 14 }}>Раздел в разработке.</p>
    </div>
  );
}

// Лёгкий «заглушечный» лоадер, пока подгружается lazy-чанк страницы.
// Виден доли секунды, поэтому без анимации — просто бледная подпись.
function ChunkLoader() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
      Загрузка…
    </div>
  );
}

export default function App() {
  return (
    // Suspense обязателен, когда есть React.lazy. Без него рендер lazy-
    // компонента «бросает» промис и React не знает, что показать. Один
    // общий Suspense вокруг всех Routes — этого достаточно: lazy-страниц
    // у нас не очень много, и они меняются по навигации.
    <Suspense fallback={<ChunkLoader />}>
    <Routes>
      <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
      {/* Сброс пароля по ссылке из письма — публичный маршрут.
          Без RequireAuth и без RedirectIfAuth: если пользователь уже
          залогинен, но открыл ссылку из почты — пусть видит форму. */}
      <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
      {/* Публичная страница оплаты по уникальной ссылке. Тоже без auth. */}
      <Route path="/pay/:token" element={<PublicPayPage />} />

      {/* Личный кабинет родителя. RequireParent — отдельный guard:
          не залогинен → /login; не parent → /students (или его дефолтная
          стартовая для роли). Внутри — Outlet из ParentShell. */}
      {/* Личный кабинет учителя. Свой шелл, дизайн совпадает с
          админ-панелью (использует те же CSS-классы .app/.main/.sidebar). */}
      <Route path="/teacher" element={<RequireTeacher><TeacherShell /></RequireTeacher>}>
        <Route index                 element={<Navigate to="schedule" replace />} />
        <Route path="schedule"       element={<TeacherSchedulePage />} />
        <Route path="journal"        element={<TeacherJournalPage />} />
        <Route path="extracurricular"         element={<TeacherExtracurricularPage />} />
        <Route path="extracurricular/session" element={<TeacherExtracurricularSessionPage />} />
        <Route path="extra"                   element={<TeacherExtraSchedulePage />} />
        <Route path="reports"                 element={<TeacherReportsPage />} />
        <Route path="reports/extras-monthly"  element={<TeacherExtrasMonthlyReport />} />
        <Route path="*"              element={<NotImplemented title="Страница не найдена" />} />
      </Route>

      <Route path="/parent" element={<RequireParent><ParentShell /></RequireParent>}>
        <Route index            element={<ParentHomePage />} />
        <Route path="journal"   element={<ParentDiaryPage />} />
        <Route path="grades"    element={<ParentGradesPage />} />
        <Route path="schedule"  element={<ParentSchedulePage />} />
        <Route path="payments"  element={<ParentPaymentsPage />} />
        <Route path="messages"        element={<ParentMessagesPage />} />
        <Route path="extracurricular" element={<ParentExtracurricularPage />} />
        <Route path="extras"          element={<ParentExtrasPage />} />
        <Route path="documents"       element={<ParentPlaceholder title="Документы" />} />
        <Route path="profile"         element={<ParentProfilePage />} />
        <Route path="*"         element={<ParentPlaceholder title="Страница не найдена" />} />
      </Route>

      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/students" element={<StudentsPage />} />
        <Route path="/years"       element={<YearsPage />} />
        <Route path="/classes"     element={<ClassesPage />} />
        <Route path="/classes/:id" element={<ClassDetailPage />} />
        <Route path="/journal"     element={<JournalPage />} />
        <Route path="/grading"     element={<GradingPage />} />
        <Route path="/schedule"    element={<SchedulePage />} />
        <Route path="/rooms"       element={<RoomsPage />} />
        <Route path="/lesson-times" element={<TimetableSlotsPage />} />
        <Route path="/subjects"    element={<SubjectsPage />} />
        <Route path="/attendance"  element={<AttendancePage />} />
        <Route path="/payments"    element={<PaymentsPage />} />
        <Route path="/invoices"    element={<NotImplemented title="Счета" />} />
        <Route path="/services"    element={<NotImplemented title="Доп. услуги" />} />
        <Route path="/tariffs"     element={<TariffsPage />} />
        <Route path="/broadcasts/email"                  element={<BroadcastsListPage />} />
        <Route path="/broadcasts/email/scheduled/:id"    element={<ScheduledBroadcastEditPage />} />
        <Route path="/broadcasts/email/:id"               element={<BroadcastEditPage />} />
        <Route path="/staff"       element={<StaffPage />} />
        <Route path="/parents"     element={<ParentsPage />} />
        <Route path="/reports"     element={<ReportsIndex />} />
        <Route path="/reports/extras-by-student-monthly" element={<ExtrasByStudentMonthlyReport />} />
        <Route path="/reports/extras-by-teacher-monthly" element={<ExtrasByTeacherMonthlyReport />} />
        <Route path="/reports/extracurricular-by-student-monthly" element={<ExtracurricularByStudentMonthlyReport />} />
        <Route path="/reports/extracurricular-by-teacher-monthly" element={<ExtracurricularByTeacherMonthlyReport />} />
        <Route path="/reports/extracurricular-popularity"         element={<ExtracurricularPopularityReport />} />
        <Route path="/reports/agreements-registry"                element={<AgreementsRegistryReport />} />
        <Route path="/reports/payments-by-new-agreements"         element={<PaymentsByNewAgreementsReport />} />
        <Route path="/reports/unpaid-next-month"          element={<UnpaidNextMonthReport />} />
        <Route path="/reports/extras-balances"            element={<ExtrasBalancesReport />} />
        <Route path="/reports/extras-subject-balance"     element={<ExtrasSubjectBalanceReport />} />
        <Route path="/reports/payments-chart"             element={<PaymentsChartReport />} />
        <Route path="/reports/unassigned-students"        element={<UnassignedStudentsReport />} />
        <Route path="/reports/students-no-lk-access"      element={<StudentsNoLkAccessReport />} />
        <Route path="/reports/class-lists"                element={<ClassListsReport />} />
        <Route path="/reports/students-on-map"            element={<StudentsOnMapReport />} />
        <Route path="/reports/terminations"               element={<TerminationsReport />} />
        <Route path="/reports/skud-first-last"            element={<SkudFirstLastReport />} />
        <Route path="/reports/skud-route"                 element={<SkudRouteReport />} />
        <Route path="/reports/api-log"                    element={<ApiLogReport />} />
        <Route path="/reports/webserver-log"              element={<WebserverLogReport />} />
        <Route path="/reports/push-outbox"                element={<PushOutboxReport />} />
        <Route path="/reports/age-sex"                    element={<AgeSexReport />} />
        <Route path="/reports/vacation-lessons"           element={<VacationLessonsReport />} />
        <Route path="/reports/parents-no-active-children" element={<ParentsNoActiveChildrenReport />} />
        <Route path="/reports/skud-parents-unlinked"      element={<SkudParentsUnlinkedReport />} />
        <Route path="/reports/mobile-devices"             element={<MobileDevicesReport />} />
        <Route path="/reports/user-actions"               element={<UserActionsReport />} />
        <Route path="/calls"       element={<CallsPage />} />
        <Route path="/calendar"    element={<HolidaysPage />} />
        <Route path="/extras/subjects" element={<ExtraSubjectsPage />} />
        <Route path="/extras/lessons"  element={<ExtraLessonsPage />} />
        <Route path="/extras/times"    element={<ExtraLessonTimesPage />} />
        <Route path="/extras/schedule" element={<ExtraSchedulePage />} />
        <Route path="/extras/charges"  element={<ExtraChargesPage />} />

        <Route path="/extracurricular/schedule"         element={<ExtracurricularSchedulePage />} />
        <Route path="/extracurricular/schedule/session" element={<ExtracurricularSessionPage />} />
        <Route path="/extracurricular/subjects" element={<ExtracurricularSubjectsPage />} />
        <Route path="/extracurricular/times"    element={<ExtracurricularTimesPage />} />
        <Route path="/extracurricular/selections"      element={<ExtracurricularSelectionsPage />} />
        <Route path="/extracurricular/selections/:id"  element={<ExtracurricularSelectionResultsPage />} />
        <Route path="/document-templates" element={<TemplatesPage />} />
        <Route path="/email-templates"    element={<EmailTemplatesPage />} />
        <Route path="/permission-presets" element={<PermissionPresetsPage />} />
        <Route path="/messages"           element={<MessagesPage />} />
        <Route path="/skud/persons" element={<SkudPersonsPage />} />
        {/* Персональные настройки текущего пользователя — доступны всем
            залогиненным, без отдельного права (вход из блока пользователя). */}
        <Route path="/me/settings" element={<UserSettingsPage />} />
        <Route path="/settings" element={<SettingsPage />}>
          <Route path="services"  element={<ServicesPanel />} />
          <Route path="dds"       element={<DdsArticlesPanel />} />
          <Route path="skud"      element={<SkudSettingsPanel />} />
          <Route path="maps"      element={<MapsSettingsPanel />} />
          <Route path="mail"      element={<MailSettingsPanel />} />
          <Route path="telephony" element={<TelephonySettingsPanel />} />
          <Route path="data_lock" element={<DataLockPanel />} />
          <Route path=":section"  element={<SettingsForm />} />
        </Route>
        <Route path="*"            element={<NotImplemented title="Страница не найдена" />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
